package com.arenaproplay.admin.ui.components.charts.svg

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/**
 * Night Arena ranked list — proportional alpha-fill bar behind each row,
 * label left / Unbounded value right.
 *
 * Styled to match the mockup's `.nb-rank` pattern:
 *   position:relative; overflow:hidden; background alpha bar via ::before or inner div.
 *
 * @param rows       list of ranked rows with label, display value, and fill fraction (0..1)
 * @param accentHex  accent color for the fill bar; default Primary blue
 * @param modifier   standard Kobweb modifier
 */
@Composable
fun RankedList(
    rows: List<RankedRow>,
    accentHex: String = "#0061FF",
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxWidth().gap(6.px)) {
        if (rows.isEmpty()) {
            Div(attrs = {
                style {
                    property("height", "40px")
                    property("background", "rgba(255,255,255,.02)")
                    property("border-radius", "8px")
                }
            })
            return@Column
        }
        rows.forEach { row ->
            RankedRowItem(row, accentHex)
        }
    }
}

@Composable
private fun RankedRowItem(row: RankedRow, accentHex: String) {
    val frac = row.fraction.coerceIn(0.0, 1.0)
    // Alpha fill: use 0.20 base opacity, matching the mockup's rgba(0,97,255,.20)
    val fillW = "${fmt(frac * 100)}%"

    Div(attrs = {
        style {
            property("position", "relative")
            property("display", "flex")
            property("justify-content", "space-between")
            property("align-items", "center")
            property("padding", "9px 12px")
            property("border-radius", "9px")
            property("overflow", "hidden")
            property("font-size", "13px")
            property("color", "#F0F4FC")
            property("font-family", "'Inter',sans-serif")
        }
    }) {
        // Alpha fill bar — absolutely positioned behind content
        Div(attrs = {
            style {
                property("position", "absolute")
                property("left", "0")
                property("top", "0")
                property("bottom", "0")
                property("width", fillW)
                property("background", "${accentHex}33") // ~20% opacity via hex alpha
                property("border-radius", "9px")
                property("transition", "width .4s ease")
            }
        })
        // Label (left) — Bug-class 1/2 fix: flex:1 + min-width:0 + overflow:hidden
        // so long screen/action names truncate rather than push the value column
        Div(attrs = {
            style {
                property("position", "relative")
                property("font-size", "13px")
                property("color", "rgba(240,244,252,.85)")
                property("z-index", "1")
                property("flex", "1")
                property("min-width", "0")
                property("overflow", "hidden")
                property("text-overflow", "ellipsis")
                property("white-space", "nowrap")
            }
        }) {
            Text(row.label)
        }
        // Value (right, Unbounded) — flex-shrink:0 keeps it always visible
        Div(attrs = {
            style {
                property("position", "relative")
                property("font-size", "13px")
                property("font-weight", "600")
                property("color", "#F0F4FC")
                property("font-family", "'Unbounded',sans-serif")
                property("z-index", "1")
                property("white-space", "nowrap")
                property("flex-shrink", "0")
            }
        }) {
            Text(row.value)
        }
    }
}
